'use client'

import { useEffect } from 'react'
import { recoverFromBoundaryError } from '@/lib/errors/error-boundary-recovery'

function reloadPage() {
  window.location.reload()
}

/**
 * Root error boundary. Replaces the entire root layout when an
 * unrecoverable error escapes the app, so it must render its own
 * `<html><body>` and cannot rely on global Tailwind layers being
 * present. Inline styles only.
 *
 * The thrown error is forwarded into Datadog RUM (when initialized). A
 * stale-deploy `ChunkLoadError` additionally spends the session's single
 * automatic reload, shared with the route boundary.
 */
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
  // `reset` is part of Next.js's contract but intentionally unused here:
  // the global boundary fires after the root layout itself has thrown,
  // so re-rendering rarely recovers; a full reload from the user is
  // usually the right next step.
  reset: () => void
}) {
  useEffect(() => {
    recoverFromBoundaryError(error)
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100svh',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Something went wrong
        </h2>
        <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>
          The error has been reported.
        </p>
        <button
          type="button"
          onClick={reloadPage}
          style={{
            border: 0,
            borderRadius: '0.75rem',
            padding: '0.625rem 1rem',
            background: '#111827',
            color: '#ffffff',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 600,
          }}
        >
          Reload page
        </button>
      </body>
    </html>
  )
}
